import React, { useEffect, useId, useRef, useState } from 'react';
import { ExpectedRow } from '../../../../interfaces/visitor-pass';
import { Collapse, InlineMessage, Presence, StatusLabel, listItemMotion } from '../../../../motion';
import { StatusPill } from '../../../status-pill/status-pill';
import { Stepper } from '../../../stepper/stepper';
import { ActionWithReason } from '../../dashboard/dashboard-shared-components/disabled-reason';
import { countOf } from '../../../../function-utils/plural';
import {
  Actions,
  Confirm,
  ConfirmText,
  ExitItem,
  Input,
  Label,
  Row,
  RowAction,
  RowLead,
  RowMain,
  RowMark,
  RowMeta,
  RowName,
  confirmMotion,
} from '../../visitor-exit/visitor-exit-page.styled';
import { initialsOf } from '../../visitor-exit/desk-list';
import { InlineAction } from '../shared/id';
import {
  categoryLabel,
  describeWritten,
  firstNameOf,
  fullNameOf,
  hostText,
  isSignOutable,
  isSignable,
  pillFor,
  rowDomId,
  windowText,
} from './expected-model';
import {
  ActionSlot,
  ConfirmButtons,
  ConfirmFields,
  ConfirmStack,
  FieldBlock,
  FieldLabel,
  InlineError,
  MetaStack,
  NoteText,
  NoteToggle,
  NotTodayText,
  RowActions,
  RowNameButton,
  ScannedCaption,
  ScannedName,
  ScannedPass,
  TickRow,
} from './expected-page.styled';

/**
 * ONE ROW OF EXPECTED TODAY — who, for whom, when, and the one tap.
 *
 * The row owns only what is local to one confirm: the party size, the note and
 * the "sign in anyway" tick, reset every time the panel opens. Everything that
 * decides WHETHER a press may go out (the in-flight gate, the pending paint,
 * the server's answer) lives on the page, keyed by row, for the same reason the
 * exit desk keeps it there: two rows must never block each other, and one row
 * must never send twice.
 *
 * ── FOCUS ──
 * Opening a confirm moves focus INTO it (the panel is a labelled group, so a
 * screen reader hears the sentence that says what will be written). Closing it
 * — Cancel, Escape, or the sign-in landing — puts focus back on the row's own
 * action, rather than leaving it on a panel that has just animated away.
 */

export type ConfirmAct = 'in' | 'out';

export interface RowInlineError {
  kind: 'not_today' | 'text';
  text: string;
}

export interface SignInChoice {
  guests: number;
  note: string;
  confirmOutsideWindow: boolean;
}

export interface ExpectedRowItemProps {
  row: ExpectedRow;
  index: number;
  /** Layout animation on (the list is short enough for the slide to read). */
  layout: boolean;
  zone: string | null | undefined;
  /** The server's clock at the last read, in ms. */
  nowMs: number;
  tone: 'plain' | 'picked';
  /** Which confirm is open on THIS row, if any. */
  open: ConfirmAct | null;
  onToggle: (act: ConfirmAct) => void;
  onClose: () => void;
  /** Why the row's actions are off, or null when they are on. */
  blockedReason: string | null;
  /** Actions are off without a reason to show yet (the shift read is pending). */
  blocked: boolean;
  pendingIn: boolean;
  pendingOut: boolean;
  /** The server asked for the explicit tick (409 PASS_OUTSIDE_WINDOW). */
  outsideRequired: boolean;
  inlineError: RowInlineError | null;
  onSignIn: (choice: SignInChoice) => void;
  onSignOut: () => void;
  onSignInAsVisitor: () => void;
  /** Open this visitor's pass (PassDetailModal, Plan 3). Left out, the name
   *  is plain text. */
  onOpenPass?: (passId: string) => void;
  /** Plan 6: this row was opened by scanning the guest's pass code — the
   *  name to show large in the confirm, so the desk compares it with the
   *  person. Null for a tap. */
  scannedName?: string | null;
}

export const ExpectedRowItem: React.FC<ExpectedRowItemProps> = ({
  row,
  index,
  layout,
  zone,
  nowMs,
  tone,
  open,
  onToggle,
  onClose,
  blockedReason,
  blocked,
  pendingIn,
  pendingOut,
  outsideRequired,
  inlineError,
  onSignIn,
  onSignOut,
  onSignInAsVisitor,
  onOpenPass,
  scannedName = null,
}) => {
  const uid = useId();
  const sentenceId = `${uid}-sentence`;
  const noteId = `${uid}-note`;
  const noteInputId = `${uid}-note-input`;
  const tickId = `${uid}-tick`;

  const name = fullNameOf(row);
  const first = firstNameOf(row);
  const pill = pillFor(row, zone, nowMs);
  const signable = isSignable(row);
  const signOutable = isSignOutable(row);
  const hasActions = signable || signOutable;
  const outsideWindow =
    row.window_state === 'early' || row.window_state === 'late' || outsideRequired;
  const maxGuests = Math.max(1, row.party_remaining);

  const [guests, setGuests] = useState(1);
  const [note, setNote] = useState('');
  const [ticked, setTicked] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);

  const inButton = useRef<HTMLButtonElement | null>(null);
  const outButton = useRef<HTMLButtonElement | null>(null);
  // One ref per panel: switching straight from one confirm to the other keeps
  // the leaving panel mounted through its exit, and a shared ref would point at
  // whichever of the two rendered last.
  const panelIn = useRef<HTMLDivElement | null>(null);
  const panelOut = useRef<HTMLDivElement | null>(null);
  // null, not `open`: a row that MOUNTS with its panel open (a ?pass= deep link)
  // still moves focus into it.
  const lastOpen = useRef<ConfirmAct | null>(null);

  // A panel that opens starts clean: one person, no note, nothing ticked. Reset
  // on the press (so no stale tick paints for a frame) and again when the page
  // opens the panel itself (a ?pass= deep link).
  const resetChoice = () => {
    setGuests(1);
    setNote('');
    setTicked(false);
  };
  useEffect(() => {
    if (open === 'in') {
      setGuests(1);
      setNote('');
      setTicked(false);
    }
  }, [open]);

  // The party can shrink under an open panel (somebody else signed in on the
  // same pass from another tablet); never ask for more than is left.
  useEffect(() => {
    if (guests > maxGuests) setGuests(maxGuests);
  }, [guests, maxGuests]);

  // FOCUS IN on open, and BACK to the row's action on close.
  useEffect(() => {
    const was = lastOpen.current;
    lastOpen.current = open;
    if (open && open !== was) {
      (open === 'in' ? panelIn.current : panelOut.current)?.focus();

      return;
    }
    if (!open && was) {
      // Only when focus was IN the panel (or has been lost with it). A panel
      // closed because the desk started typing in the search box must not
      // drag focus out of the box mid-word.
      const leaving = was === 'in' ? panelIn.current : panelOut.current;
      const active = document.activeElement;
      const focusWasHere =
        !active ||
        active === document.body ||
        (leaving !== null && leaving.contains(active));
      if (!focusWasHere) return;
      const back =
        (was === 'in' ? inButton.current : outButton.current) ||
        inButton.current ||
        outButton.current;
      back?.focus();
    }
  }, [open]);

  const onPanelKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
    }
  };

  const metaOne = [
    row.company || null,
    categoryLabel(row),
    `for ${hostText(row)}`,
  ]
    .filter(Boolean)
    .join(' · ');
  const metaTwo = [
    windowText(row, zone),
    row.max_party > 1 ? `up to ${countOf(row.max_party, 'person', 'people')}` : null,
    row.created_via === 'staff' ? 'Added by building' : 'Added by resident',
  ]
    .filter(Boolean)
    .join(' · ');

  const inBusy = pendingIn;
  const outBusy = pendingOut;
  const confirmInDisabled = inBusy || blocked || (outsideWindow && !ticked);

  return (
    <ExitItem
      id={rowDomId(row)}
      data-pass-id={row.pass_id}
      {...listItemMotion(index)}
      layout={layout}
    >
      <Row tone={tone}>
        <RowLead>
          <RowMark tone={tone} aria-hidden="true">
            {initialsOf(name)}
          </RowMark>
          <RowMain>
            {onOpenPass ? (
              <RowNameButton type="button" onClick={() => onOpenPass(row.pass_id)}>
                {name}
              </RowNameButton>
            ) : (
              <RowName>{name}</RowName>
            )}
            <RowMeta>{metaOne}</RowMeta>
            <RowMeta>{metaTwo}</RowMeta>
            <MetaStack>
              <StatusPill tone={pill.tone} dot>
                {pill.text}
              </StatusPill>
              {row.note ? (
                <>
                  <NoteToggle
                    type="button"
                    aria-expanded={noteOpen}
                    aria-controls={noteId}
                    onClick={() => setNoteOpen(v => !v)}
                  >
                    {noteOpen ? 'Hide note' : 'Show note'}
                  </NoteToggle>
                  <Collapse open={noteOpen} id={noteId}>
                    <NoteText>{row.note}</NoteText>
                  </Collapse>
                </>
              ) : null}
            </MetaStack>
          </RowMain>
        </RowLead>
        {hasActions ? (
          <Actions>
            <ActionSlot>
              <ActionWithReason reason={blockedReason}>
                {describedBy => (
                  <RowActions>
                    {signable ? (
                      <RowAction
                        ref={inButton}
                        type="button"
                        variant={open === 'in' ? 'quiet' : 'primary'}
                        aria-describedby={describedBy}
                        aria-expanded={open === 'in'}
                        // Cancel stays pressable even when the actions go off
                        // under an open panel; only OPENING is refused.
                        disabled={(blocked && open !== 'in') || inBusy}
                        onClick={() => {
                          if (open !== 'in') resetChoice();
                          onToggle('in');
                        }}
                      >
                        {open === 'in' ? 'Cancel' : 'Sign in'}
                      </RowAction>
                    ) : null}
                    {signOutable ? (
                      <RowAction
                        ref={outButton}
                        type="button"
                        variant={open === 'out' ? 'quiet' : signable ? 'quiet' : 'primary'}
                        aria-describedby={describedBy}
                        aria-expanded={open === 'out'}
                        disabled={(blocked && open !== 'out') || outBusy}
                        onClick={() => onToggle('out')}
                      >
                        {open === 'out' ? 'Cancel' : 'Sign out'}
                      </RowAction>
                    ) : null}
                  </RowActions>
                )}
              </ActionWithReason>
            </ActionSlot>
          </Actions>
        ) : null}
      </Row>
      <Presence>
        {open === 'in' ? (
          <Confirm key="confirm-in" {...confirmMotion}>
            <ConfirmStack
              ref={panelIn}
              tabIndex={-1}
              role="group"
              aria-labelledby={sentenceId}
              onKeyDown={onPanelKey}
            >
              {scannedName ? (
                <ScannedPass>
                  <ScannedCaption>Scanned pass</ScannedCaption>
                  <ScannedName>{scannedName}</ScannedName>
                </ScannedPass>
              ) : null}
              <ConfirmText id={sentenceId}>{describeWritten(row)}</ConfirmText>
              <ConfirmFields>
                {maxGuests > 1 ? (
                  <FieldBlock>
                    <FieldLabel aria-hidden="true">People</FieldLabel>
                    <Stepper
                      value={guests}
                      min={1}
                      max={maxGuests}
                      label="people"
                      disabled={inBusy}
                      onChange={setGuests}
                    />
                  </FieldBlock>
                ) : null}
                <FieldBlock $grow>
                  <Label htmlFor={noteInputId}>Note (optional)</Label>
                  <Input
                    id={noteInputId}
                    value={note}
                    maxLength={280}
                    disabled={inBusy}
                    onChange={e => setNote(e.target.value)}
                  />
                </FieldBlock>
              </ConfirmFields>
              {outsideWindow ? (
                <TickRow htmlFor={tickId}>
                  <input
                    id={tickId}
                    type="checkbox"
                    checked={ticked}
                    disabled={inBusy}
                    onChange={e => setTicked(e.target.checked)}
                  />
                  <span>Outside the pass window. Sign in anyway?</span>
                </TickRow>
              ) : null}
              <InlineMessage
                role="alert"
                message={
                  inlineError ? (
                    <InlineError>
                      {inlineError.kind === 'not_today' ? (
                        <>
                          <NotTodayText>{inlineError.text}</NotTodayText>
                          <InlineAction type="button" onClick={onSignInAsVisitor}>
                            Sign in as a visitor
                          </InlineAction>
                        </>
                      ) : (
                        <NotTodayText>{inlineError.text}</NotTodayText>
                      )}
                    </InlineError>
                  ) : null
                }
              />
              <ConfirmButtons>
                <RowAction
                  type="button"
                  variant="primary"
                  disabled={confirmInDisabled}
                  aria-busy={inBusy || undefined}
                  onClick={() =>
                    onSignIn({
                      guests: Math.min(guests, maxGuests),
                      note,
                      confirmOutsideWindow: outsideWindow && ticked,
                    })
                  }
                >
                  <StatusLabel
                    state={inBusy ? 'busy' : 'idle'}
                    labels={{ idle: `Confirm: sign ${first} in`, busy: 'Signing in…' }}
                  />
                </RowAction>
              </ConfirmButtons>
            </ConfirmStack>
          </Confirm>
        ) : null}
        {open === 'out' ? (
          <Confirm key="confirm-out" {...confirmMotion}>
            <ConfirmStack
              ref={panelOut}
              tabIndex={-1}
              role="group"
              aria-labelledby={sentenceId}
              onKeyDown={onPanelKey}
            >
              <ConfirmText id={sentenceId}>
                {`Record that ${name} left, now?`}
              </ConfirmText>
              <InlineMessage
                role="alert"
                message={
                  inlineError ? (
                    <InlineError>
                      <NotTodayText>{inlineError.text}</NotTodayText>
                    </InlineError>
                  ) : null
                }
              />
              <ConfirmButtons>
                <RowAction
                  type="button"
                  variant="danger"
                  disabled={outBusy || blocked}
                  aria-busy={outBusy || undefined}
                  onClick={onSignOut}
                >
                  <StatusLabel
                    state={outBusy ? 'busy' : 'idle'}
                    labels={{ idle: `Confirm: sign ${first} out`, busy: 'Signing out…' }}
                  />
                </RowAction>
              </ConfirmButtons>
            </ConfirmStack>
          </Confirm>
        ) : null}
      </Presence>
    </ExitItem>
  );
};
